import {
  CloseOutlined,
  RowElement,
  Spacer,
  SpinnerDS,
  TextCapitalized,
} from "@qivia/ui";
import {
  ClosedEye,
  OpenEye,
  QiviaLogoBadge,
} from "@qivia/ui/src/assets/assets";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  acknowledgePlatformNotificationAsync,
  getUnacknowledgedPlatformNotificationsAsync,
  getUnacknowledgedPlatformNotificationsCountByTypeAsync,
  selectGetUnacknowledgedPlatformNotificationsCountByTypeStatus,
  selectGetUnacknowledgedPlatformNotificationsStatus,
  selectUnacknowledgedPlatformNotificationsList,
} from "./notificationsSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompany } from "../homeSlice";
import { useTranslation } from "react-i18next";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import {
  fakeNotificationsByCategoriesDisplayedForDemo,
  notificationsCategories,
  notificationsList,
} from "./lib";
import {
  GetUnacknowledgedPlatformNotificationsListType,
  NotificationsCategoriesType,
} from "./notificationsApi";
import "../../../i18n/i18n";

export const HistoryTab = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useAppSelector(selectCompany);
  const [categoriesDisplayed, setCategoriesDisplayed] = useState<
    NotificationsCategoriesType[]
  >(() => notificationsCategories);
  const [
    fakeNotificationsDisplayedForDemo,
    setFakeNotificationsDisplayedForDemo,
  ] = useState<GetUnacknowledgedPlatformNotificationsListType[]>(
    fakeNotificationsByCategoriesDisplayedForDemo,
  );

  const unacknowledgedPlatformNotificationsList = useAppSelector(
    selectUnacknowledgedPlatformNotificationsList,
  );
  const getUnacknowledgedPlatformNotificationsStatus = useAppSelector(
    selectGetUnacknowledgedPlatformNotificationsStatus,
  );
  const getUnacknowledgedPlatformNotificationsCountByTypeStatus =
    useAppSelector(
      selectGetUnacknowledgedPlatformNotificationsCountByTypeStatus,
    );
  const { t } = useTranslation();

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const handleCloseNotification = useCallback(
    (uuid: string) => {
      void dispatch(acknowledgePlatformNotificationAsync(uuid));
    },
    [dispatch],
  );

  const allPlatformNotificationsDisplayed: {
    uuid: string;
    title: string;
    description: string;
    actionPath: string | null;
  }[] = useMemo(() => {
    if (!unacknowledgedPlatformNotificationsList) return [];

    const listDisplayed = isDemoEnvironment()
      ? [
          ...unacknowledgedPlatformNotificationsList,
          ...fakeNotificationsDisplayedForDemo,
        ]
      : unacknowledgedPlatformNotificationsList;

    const filteredNotificationsByCategoriesDisplayed = listDisplayed.filter(
      (notif) => {
        const categoryOfNotification = notificationsList.find(
          (n) => n.type === notif.type,
        )?.category;
        return (
          categoryOfNotification &&
          categoriesDisplayed.includes(categoryOfNotification)
        );
      },
    );

    return filteredNotificationsByCategoriesDisplayed.map((notif) => {
      return {
        uuid: notif.uuid,
        title: notif.title ?? t(`notifications.title.${String(notif.type)}`),
        description: notif.description ?? "",
        actionPath: notif.actionPath ?? null,
      };
    });
  }, [
    categoriesDisplayed,
    fakeNotificationsDisplayedForDemo,
    isDemoEnvironment,
    t,
    unacknowledgedPlatformNotificationsList,
  ]);

  useEffect(() => {
    if (
      company &&
      getUnacknowledgedPlatformNotificationsCountByTypeStatus === "idle"
    ) {
      void dispatch(
        getUnacknowledgedPlatformNotificationsCountByTypeAsync(company.uuid),
      );
    }
  }, [
    company,
    dispatch,
    getUnacknowledgedPlatformNotificationsCountByTypeStatus,
  ]);

  useEffect(() => {
    if (company && getUnacknowledgedPlatformNotificationsStatus === "idle") {
      void dispatch(getUnacknowledgedPlatformNotificationsAsync(company.uuid));
    }
  }, [company, dispatch, getUnacknowledgedPlatformNotificationsStatus]);

  if (params.tab !== "history") {
    return;
  }

  const isFirstLoading =
    !unacknowledgedPlatformNotificationsList &&
    (getUnacknowledgedPlatformNotificationsStatus === "idle" ||
      getUnacknowledgedPlatformNotificationsStatus === "processing");

  return (
    <StyledFlex>
      <StyledHistory>
        {isFirstLoading && (
          <StyledLoader>
            <SpinnerDS />
          </StyledLoader>
        )}
        {unacknowledgedPlatformNotificationsList &&
          allPlatformNotificationsDisplayed.length === 0 && (
            <StyledEmpty>
              <TextCapitalized>
                {t("notifications.history.empty")}
              </TextCapitalized>
            </StyledEmpty>
          )}
        {allPlatformNotificationsDisplayed.map((notif) => (
          <div key={notif.uuid}>
            <RowElement
              key={notif.uuid}
              text={notif.title}
              subText={notif.description}
              leftIcon={<QiviaLogoBadge />}
              borderRadius={0.5}
              size="XL"
              rightElement={
                <StyledClose>
                  <CloseOutlined
                    onClick={(event: React.DragEvent<HTMLDivElement>) => {
                      event.stopPropagation();
                      isDemoEnvironment() &&
                        setFakeNotificationsDisplayedForDemo(
                          fakeNotificationsDisplayedForDemo.filter(
                            (n) => n.uuid !== notif.uuid,
                          ),
                        );
                      handleCloseNotification(notif.uuid);
                    }}
                  />
                </StyledClose>
              }
              onClickRow={
                notif.actionPath
                  ? () => {
                      notif.actionPath && navigate(notif.actionPath);
                    }
                  : undefined
              }
            />
            <Spacer y={0.5} />
          </div>
        ))}
      </StyledHistory>
      <Spacer x={2} />
      <Filters
        categoriesDisplayed={categoriesDisplayed}
        setCategoriesDisplayed={setCategoriesDisplayed}
      />
      <Spacer x={1} />
    </StyledFlex>
  );
};

export const Filters = (payload: {
  categoriesDisplayed: NotificationsCategoriesType[];
  setCategoriesDisplayed: (category: NotificationsCategoriesType[]) => void;
}) => {
  const { t } = useTranslation();
  const removeCategory = useCallback(
    (category: NotificationsCategoriesType) => {
      payload.setCategoriesDisplayed(
        payload.categoriesDisplayed.filter((c) => c !== category),
      );
    },
    [payload],
  );

  const addCategory = useCallback(
    (category: NotificationsCategoriesType) => {
      payload.setCategoriesDisplayed([
        ...payload.categoriesDisplayed,
        category,
      ]);
    },
    [payload],
  );

  return (
    <StyledFiltersContainer>
      <TextCapitalized>{t("notifications.history.filters")}</TextCapitalized>
      <Spacer y={1} />
      <StyledFilters>
        {notificationsCategories.map((category) => {
          const isDisplayed = payload.categoriesDisplayed.includes(category);

          return (
            <StyledFilter
              key={category}
              onClick={() => {
                if (isDisplayed) {
                  removeCategory(category);
                } else {
                  addCategory(category);
                }
              }}
            >
              <Spacer y={0.5} />
              <StyledFlex>
                <Spacer x={0.5} />
                {isDisplayed ? <OpenEye /> : <ClosedEye />}
                <Spacer x={0.5} />
                <TextCapitalized>
                  {t(`notifications.category.${category}`)}
                </TextCapitalized>
                <Spacer x={0.5} />
              </StyledFlex>
              <Spacer y={0.5} />
            </StyledFilter>
          );
        })}
      </StyledFilters>
    </StyledFiltersContainer>
  );
};
const StyledFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const StyledFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 18rem;
  max-width: 18rem;
  ${typographies["Header/H5"]}
`;

const StyledFilter = styled.div`
  display: flex;
  flex-direction: column;
  border: 0.0625rem solid ${colors["colors/borders/input/default"]};
  width: fit-content;
  border-radius: 0.5rem;
  ${typographies["Body/M"]}
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
  & path {
    fill: black;
  }
`;

const StyledClose = styled.div`
  cursor: pointer;
  & svg {
    fill: ${colors["colors/text/lightGrey"]};
  }
`;

const StyledLoader = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledEmpty = styled.div`
  display: flex;
  justify-content: center;
  ${typographies["Body/M"]}
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledHistory = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 30rem;
`;
